import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Package, ShoppingCart, Camera, ArrowRight, Upload, Image as ImageIcon
} from 'lucide-react';
import { Button } from './button';
import { optimizeImage, uploadImageToStorage, getSignedImageUrl } from '../../lib/api';
import { useNoshStore } from '../../lib/store';
import { supabase } from '../../lib/supabase';

interface IngredientSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSourceSelected: (sourceType: 'inventory' | 'mixed' | 'photo', photoIngredients?: string[]) => void;
  inventoryCount: number;
  isMobile?: boolean;
}

export interface DetectedIngredients {
  ingredients: string[];
  confidence_scores: Record<string, number>;
  total_ingredients_found: number;
  overall_confidence: number;
  detection_id?: string;
}

export const IngredientSourceModal: React.FC<IngredientSourceModalProps> = ({ 
  isOpen, 
  onClose, 
  onSourceSelected,
  inventoryCount,
  isMobile = false
}) => {
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectionError, setDetectionError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  // File input refs for different capture methods
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  // Get the store function for saving preferences
  const { saveMealGenerationPreferences, getMealGenerationPreferences } = useNoshStore();

  // Detect ingredients from photo
  const detectIngredientsFromPhoto = async (file: File): Promise<DetectedIngredients> => {
    try {
      console.log('🔍 Starting ingredient detection for file:', {
        name: file.name,
        originalSize: Math.round(file.size / 1024) + 'KB',
        type: file.type,
        timestamp: new Date().toISOString(),
        version: 'v2.1-compression-fix'
      });
      
      // Use AI-optimized compression for ingredient detection (higher quality, larger file size)
      const optimizedFile = await optimizeImage(file, 1200, 0.9, 'unknown', 'ai-analysis');
      
              console.log('✅ Image optimized for AI analysis:', {
          originalSize: Math.round(file.size / 1024) + 'KB',
          optimizedSize: Math.round(optimizedFile.size / 1024) + 'KB',
          compressionRatio: Math.round(((file.size - optimizedFile.size) / file.size) * 100) + '%',
          purpose: 'ai-analysis',
          wasCompressed: file.size !== optimizedFile.size
        });
      
      // Get current user session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error('Please log in to use ingredient detection');
      }
      
              // Upload to storage
        const uploadResult = await uploadImageToStorage(optimizedFile, session.user.id);
        console.log('✅ Image uploaded to storage:', uploadResult);
        
        // Generate signed URL for AI processing
        const signedUrl = await getSignedImageUrl(uploadResult, 300);
        
        console.log('🔍 Image URLs for inspection:', {
          storagePath: uploadResult,
          signedUrl: signedUrl.substring(0, 100) + '...',
          fullSignedUrl: signedUrl,
          note: 'Use fullSignedUrl to see actual stored image quality (expires in 5 min)'
        });
      
      // Call the detect-ingredients edge function
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/detect-ingredients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          image: signedUrl
        }),
      });
      
      const result = await response.json();
      console.log('🤖 Ingredient detection result:', result);
      
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to detect ingredients');
      }
      
      // Check if no ingredients were detected
      if (!result.data?.ingredients || result.data.ingredients.length === 0) {
        throw new Error('No food detected. Try again with a clearer photo showing ingredients.');
      }
      
      return result.data;
      
    } catch (error) {
      console.error('❌ Ingredient detection failed:', error);
      throw error;
    }
  };

  // Handle photo scan - directly trigger appropriate input based on device
  const handlePhotoScan = async () => {
    if (isDetecting) return;
    
    if (isMobile) {
      // Mobile: directly open camera
      if (cameraInputRef.current) {
        cameraInputRef.current.click();
      }
    } else {
      // Desktop: directly open file upload
      if (uploadInputRef.current) {
        uploadInputRef.current.click();
      }
    }
  };

  // Handle file selection (from camera or upload)
  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // CRITICAL DEBUG: Check if this function is called at all
    console.log('🚨 INGREDIENT MODAL DEBUG - File selected:', {
      fileName: file.name,
      fileSize: Math.round(file.size / 1024) + 'KB',
      timestamp: new Date().toISOString(),
      codeVersion: 'v2.1-latest'
    });
    
    setSelectedFile(file);
    setDetectionError(null);
    setIsDetecting(true);
    
    try {
      console.log('📸 Processing selected file:', file.name);
      
      const detectedIngredients = await detectIngredientsFromPhoto(file);
      
      console.log('🎉 Successfully detected ingredients:', detectedIngredients.ingredients);
      
      // CRITICAL DEBUG: Log what we're about to pass to parent
      console.log('🚨 [IngredientSourceModal] CRITICAL DEBUG - About to call onSourceSelected with:', {
        sourceType: 'photo',
        detectedIngredients: detectedIngredients.ingredients,
        ingredientsCount: detectedIngredients.ingredients?.length || 0,
        ingredientsType: typeof detectedIngredients.ingredients,
        isArray: Array.isArray(detectedIngredients.ingredients)
      });
      
      // Close modal and pass detected ingredients to parent
      onClose();
      onSourceSelected('photo', detectedIngredients.ingredients);
      
    } catch (error) {
      console.error('❌ Error processing photo:', error);
      setDetectionError(error instanceof Error ? error.message : 'Failed to detect ingredients');
    } finally {
      setIsDetecting(false);
      setSelectedFile(null);
      // Reset file inputs
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (uploadInputRef.current) uploadInputRef.current.value = '';
    }
  };

  // Save ingredient source preference to database
  const saveIngredientSourcePreference = async (source: 'inventory' | 'mixed') => {
    try {
      console.log('💾 Saving ingredient source preference:', source);
      
      // Get current preferences to preserve other settings
      const currentPrefs = await getMealGenerationPreferences();
      
      const preferencesToSave = {
        defaultSource: source,
        includeShoppingItems: currentPrefs?.includeShoppingItems ?? false,
        prioritizeExpiring: currentPrefs?.prioritizeExpiring ?? true,
        maxCookTime: currentPrefs?.maxCookTime ?? 30,
        preferredServings: currentPrefs?.preferredServings ?? 2,
        preferredDifficulty: currentPrefs?.preferredDifficulty ?? 'any',
        preferredMealType: currentPrefs?.preferredMealType ?? 'any',
        additionalProtein: currentPrefs?.additionalProtein ?? false,
        additionalVegetables: currentPrefs?.additionalVegetables ?? false,
        preferredCuisineStyle: currentPrefs?.preferredCuisineStyle ?? 'any',
        preferredSpiceLevel: currentPrefs?.preferredSpiceLevel ?? 'medium',
        healthyFocus: currentPrefs?.healthyFocus ?? false,
        budgetFriendly: currentPrefs?.budgetFriendly ?? false,
      };
      
      await saveMealGenerationPreferences(preferencesToSave);
      console.log('✅ Ingredient source preference saved successfully');
    } catch (error) {
      console.error('❌ Failed to save ingredient source preference:', error);
      // Continue anyway - saving preference is not critical for the immediate flow
    }
  };

  // Handle source selection
  const handleSourceSelect = async (source: 'inventory' | 'mixed') => {
    console.log('🎯 Ingredient source selected:', source);
    
    // Save the preference to database first
    await saveIngredientSourcePreference(source);
    
    // Close the modal
    onClose();
    
    // Then proceed with the selection
    onSourceSelected(source);
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className={`font-heading font-bold text-gray-900 dark:text-gray-100 ${isMobile ? 'text-xl' : 'text-lg'}`}>
          Choose Your Ingredient Source
        </h2>
        <p className={`text-gray-600 dark:text-gray-400 ${isMobile ? 'text-xs' : 'text-sm'}`}>
          Select where AI should find ingredients for your recipe
        </p>
      </div>

      {/* Error Message */}
      {detectionError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
            <div className="flex-1">
              <p className="text-red-700 dark:text-red-300 text-sm font-medium mb-2">
                {detectionError.split('\n')[0]}
              </p>
              {detectionError.includes('•') && (
                <div className="text-red-600 dark:text-red-400 text-xs space-y-1">
                  {detectionError.split('\n').slice(1).map((tip, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <span className="text-red-400 mt-0.5">•</span>
                      <span>{tip.replace('•', '').trim()}</span>
                    </div>
                  ))}
                </div>
              )}
              <Button
                onClick={() => setDetectionError(null)}
                variant="ghost"
                size="sm"
                className="mt-3 text-red-600 hover:text-red-700 dark:text-red-400 p-0 h-auto font-medium"
              >
                Try Again
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Source Options */}
      <div className={`space-y-${isMobile ? '3' : '3'}`}>
        
        {/* Inventory Only Option */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleSourceSelect('inventory')}
          disabled={isDetecting}
          className={`relative w-full ${isMobile ? 'p-4' : 'p-4'} rounded-2xl border-2 border-gray-200 dark:border-gray-700 hover:border-primary/50 dark:hover:border-primary/50 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed ${isMobile ? 'bg-muted hover:bg-muted/80' : ''}`}
        >
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <div className={`${isMobile ? 'w-12 h-12' : 'w-10 h-10'} rounded-xl bg-blue-100 dark:bg-blue-800/30 flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-700/40 transition-colors`}>
                <Package size={isMobile ? 24 : 20} className="text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <div className="flex-1 text-left">
              <h3 className={`font-semibold text-gray-900 dark:text-gray-100 ${isMobile ? 'text-lg mb-1' : 'text-base mb-1'}`}>
                Use Inventory Only
              </h3>
              <p className={`text-gray-600 dark:text-gray-400 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                Create recipes from your current <span className="font-medium text-primary">{inventoryCount} items</span>
              </p>
            </div>
            <div className="flex-shrink-0">
              <ArrowRight size={isMobile ? 20 : 16} className="text-gray-400 group-hover:text-primary transition-colors" />
            </div>
          </div>
        </motion.button>

        {/* Mixed with Shopping Option */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleSourceSelect('mixed')}
          disabled={isDetecting}
          className={`relative w-full ${isMobile ? 'p-4' : 'p-4'} rounded-2xl border-2 border-gray-200 dark:border-gray-700 hover:border-primary/50 dark:hover:border-primary/50 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed ${isMobile ? 'bg-muted hover:bg-muted/80' : ''}`}
        >
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <div className={`${isMobile ? 'w-12 h-12' : 'w-10 h-10'} rounded-xl bg-green-100 dark:bg-green-800/30 flex items-center justify-center group-hover:bg-green-200 dark:group-hover:bg-green-700/40 transition-colors`}>
                <ShoppingCart size={isMobile ? 24 : 20} className="text-green-600 dark:text-green-400" />
              </div>
            </div>
            <div className="flex-1 text-left">
              <h3 className={`font-semibold text-gray-900 dark:text-gray-100 ${isMobile ? 'text-lg mb-1' : 'text-base mb-1'}`}>
                Discover New Flavors
              </h3>
              <p className={`text-gray-600 dark:text-gray-400 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                Create recipes using what you have plus new ingredient suggestions
              </p>
            </div>
            <div className="flex-shrink-0">
              <ArrowRight size={isMobile ? 20 : 16} className="text-gray-400 group-hover:text-primary transition-colors" />
            </div>
          </div>
        </motion.button>

        {/* Photo Ingredients Option */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handlePhotoScan}
          disabled={isDetecting}
          className={`relative w-full ${isMobile ? 'p-4' : 'p-4'} rounded-2xl border-2 border-gray-200 dark:border-gray-700 hover:border-primary/50 dark:hover:border-primary/50 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed ${isMobile ? 'bg-muted hover:bg-muted/80' : ''}`}
        >
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <div className={`${isMobile ? 'w-12 h-12' : 'w-10 h-10'} rounded-xl bg-purple-100 dark:bg-purple-800/30 flex items-center justify-center group-hover:bg-purple-200 dark:group-hover:bg-purple-700/40 transition-colors`}>
                {isDetecting ? (
                  <div className={`animate-spin rounded-full ${isMobile ? 'h-6 w-6' : 'h-5 w-5'} border-b-2 border-purple-600`}></div>
                ) : (
                  <Camera size={isMobile ? 24 : 20} className="text-purple-600 dark:text-purple-400" />
                )}
              </div>
            </div>
            <div className="flex-1 text-left">
              <h3 className={`font-semibold text-gray-900 dark:text-gray-100 ${isMobile ? 'text-lg mb-1' : 'text-base mb-1'}`}>
                {isDetecting ? 'Detecting Ingredients...' : 'Scan Photo Ingredients'}
              </h3>
              <p className={`text-gray-600 dark:text-gray-400 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                {isDetecting 
                  ? 'AI is analyzing your photo...'
                  : 'From bench to meal - all from a single photo'
                }
              </p>
            </div>
            <div className="flex-shrink-0">
              {!isDetecting && (
                <ArrowRight size={isMobile ? 20 : 16} className="text-gray-400 group-hover:text-primary transition-colors" />
              )}
            </div>
          </div>
        </motion.button>
      </div>

      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
        capture="environment"
        onChange={handleFileSelected}
        className="hidden"
      />
      <input
        ref={uploadInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
        onChange={handleFileSelected}
        className="hidden"
      />
    </div>
  );

  const modalJSX = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "linear" }}
            onClick={onClose}
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(8px)',
              zIndex: 9997, // Lower than meal generation modal
              margin: 0, 
              padding: 0 
            }}
          />

          {/* Modal - Both mobile and desktop use centered popup now */}
          <div 
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9998,
              margin: 0, 
              padding: isMobile ? '1rem' : '0 1rem' 
            }}
            onClick={onClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ 
                duration: 0.25,
                ease: "linear"
              }}
              className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl border border-gray-200/50 dark:border-gray-700/50 shadow-2xl w-full max-h-[90vh] overflow-hidden"
              style={{
                maxWidth: isMobile ? '100%' : '28rem'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`${isMobile ? 'p-6' : 'p-6'} relative`}>
                {/* Close Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  disabled={isDetecting}
                  className={`absolute ${isMobile ? 'top-4 right-4' : 'top-6 right-6'} rounded-full z-10`}
                >
                  <X size={24} />
                </Button>
                {modalContent}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalJSX, document.body);
}; 